import { useCallback, useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { SceneEngine } from '../engine/SceneEngine'
import { ControlsEngine, type HoverInfo } from '../engine/ControlsEngine'
import { BuildingRenderer } from '../engine/BuildingRenderer'
import { TopologyRenderer } from '../engine/TopologyRenderer'
import { RouteRenderer } from '../engine/RouteRenderer'
import { useBuildingStore } from '../stores/buildingStore'
import { useViewStore } from '../stores/viewStore'
import { useSelectionStore } from '../stores/selectionStore'
import { useRealtimeStore } from '../stores/realtimeStore'
import { useUIStore } from '../stores/uiStore'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuthStore } from '../stores/authStore'
import { siteApi } from '../services/siteApi'
import { zoneApi } from '../services/zoneApi'
import { wallApi } from '../services/wallApi'
import Tooltip3D from './common/Tooltip3D'

export default function ThreeCanvas() {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<SceneEngine | null>(null)
  const controlsRef = useRef<ControlsEngine | null>(null)
  const buildingRef = useRef<BuildingRenderer | null>(null)
  const topologyRef = useRef<TopologyRenderer | null>(null)
  const routeRef = useRef<RouteRenderer | null>(null)

  // Hover state for tooltip
  const [hoveredObject, setHoveredObject] = useState<HoverInfo | null>(null)

  // Store state
  const floors = useBuildingStore((s) => s.floors)
  const walls = useBuildingStore((s) => s.walls)
  const locations = useBuildingStore((s) => s.locations)
  const badges = useBuildingStore((s) => s.badges)
  const pathSegments = useBuildingStore((s) => s.pathSegments)
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const explodeValue = useViewStore((s) => s.explodeValue)
  const wallsVisible = useViewStore((s) => s.wallsVisible)
  const ifcWallsVisible = useViewStore((s) => s.ifcWallsVisible)
  const floorplansVisible = useViewStore((s) => s.floorplansVisible)
  const zonesVisible = useViewStore((s) => s.zonesVisible)
  const occupantsVisible = useViewStore((s) => s.occupantsVisible)
  const pathSegmentsVisible = useViewStore((s) => s.pathSegmentsVisible)
  const isolatedFloor = useViewStore((s) => s.isolatedFloor)
  const selectedFloor = useViewStore((s) => s.selectedFloor)
  const currentRoute = useViewStore((s) => s.currentRoute)
  const is2DMode = useViewStore((s) => s.is2DMode)
  const setFocusCameraFn = useViewStore((s) => s.setFocusCameraFn)
  const editMode = useSelectionStore((s) => s.editMode)
  const selectedWall = useSelectionStore((s) => s.selectedWall)
  const selectZone = useSelectionStore((s) => s.selectZone)
  const selectWall = useSelectionStore((s) => s.selectWall)
  const liveMode = useViewStore((s) => s.liveMode)
  const positions = useRealtimeStore((s) => s.positions)
  const topologyNodes = useRealtimeStore((s) => s.topologyNodes)
  const topologyEdges = useRealtimeStore((s) => s.topologyEdges)
  const showTopology = useRealtimeStore((s) => s.showTopology)
  const showOptimalOnly = useRealtimeStore((s) => s.showOptimalOnly)
  const filterOffline = useRealtimeStore((s) => s.filterOffline)
  const rssiThreshold = useRealtimeStore((s) => s.rssiThreshold)
  const ifcRotation = useViewStore((s) => s.ifcRotation)
  const setActiveTab = useUIStore((s) => s.setActiveTab)

  // WebSocket connection
  useWebSocket(liveMode ? currentSiteId : null)

  // Stable callback for hover changes
  const handleHoverChange = useCallback((info: HoverInfo | null) => {
    setHoveredObject(info)
  }, [])

  // Init engine
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    canvas.width = container.clientWidth
    canvas.height = container.clientHeight

    const engine = new SceneEngine(canvas)
    const controls = new ControlsEngine(engine.camera, canvas)
    const building = new BuildingRenderer(engine.scene)
    const topology = new TopologyRenderer()
    engine.scene.add(topology.group)
    const route = new RouteRenderer()
    engine.scene.add(route.group)
    controls.setScene(engine.scene)
    engineRef.current = engine
    controlsRef.current = controls
    buildingRef.current = building
    topologyRef.current = topology
    routeRef.current = route

    // Connect hover callback
    controls.onHoverChange = handleHoverChange

    engine.animate(() => {
      controls.update()
      building.update()
    })

    // Expose focusOnPosition via viewStore
    setFocusCameraFn((x: number, y: number, z: number) => {
      controls.focusOnPosition(x, y, z)
    })

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const { width, height } = entry.contentRect
      engine.resize(width, height)
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
      route.dispose()
      topology.dispose()
      building.dispose()
      controls.dispose()
      engine.dispose()
      engineRef.current = null
      controlsRef.current = null
      buildingRef.current = null
      topologyRef.current = null
      routeRef.current = null
      setFocusCameraFn(null)
    }
  }, [handleHoverChange, setFocusCameraFn])

  // Bridge: floors
  useEffect(() => {
    const building = buildingRef.current
    if (!building || !currentSiteId || floors.length === 0) return

    const loadFloorplans = async () => {
      const urls = new Map<number, string>()
      for (const floor of floors) {
        if (floor.floorplan_minio_key) {
          try {
            const url = await siteApi.getFloorplanUrl(currentSiteId, floor.index)
            urls.set(floor.index, url)
          } catch {
            // No floorplan available
          }
        }
      }
      console.log('[ThreeCanvas] setFloors:', floors.length, 'floorplans:', urls.size)
      building.setFloors(floors, urls)
    }
    loadFloorplans()
  }, [floors, currentSiteId])

  // Bridge: walls (+ trigger zone recalculation)
  useEffect(() => {
    const building = buildingRef.current
    if (!building) return
    console.log('[ThreeCanvas] setWalls:', walls.length)
    building.setWalls(walls)

    // Recalculate zone clipping after walls change
    if (building.zones.size > 0) {
      // Small delay to ensure wall meshes are in scene
      requestAnimationFrame(() => {
        building.recomputeZoneClipping()
      })
    }
  }, [walls])

  // Bridge: IFC walls
  useEffect(() => {
    const building = buildingRef.current
    if (!building || !currentSiteId) return

    const loadIFC = async () => {
      try {
        const token = useAuthStore.getState().token ?? undefined
        const geometryUrls = await siteApi.getIFCGeometryUrls(currentSiteId)
        console.log('[ThreeCanvas] IFC geometry URLs:', Object.keys(geometryUrls))
        await building.setIFCWalls(geometryUrls, token)

        // Apply IFC rotation from site settings
        const sites = useBuildingStore.getState().sites
        const currentSite = sites.find(s => s.id === currentSiteId)
        if (currentSite?.ifc_rotation) {
          building.wallRenderer.setIFCRotation(currentSite.ifc_rotation)
        }

        // Recalculate zones after IFC walls loaded
        if (building.zones.size > 0) {
          building.recomputeZoneClipping()
        }
      } catch {
        // No IFC walls or endpoint not available
      }
    }
    loadIFC()
  }, [floors, currentSiteId])

  // Bridge: locations (zones)
  useEffect(() => {
    const building = buildingRef.current
    if (!building) return
    console.log('[ThreeCanvas] setLocations:', locations.length)
    building.setLocations(locations)

    // Recalculate zone clipping after locations change
    requestAnimationFrame(() => {
      building.recomputeZoneClipping()
    })
  }, [locations])

  // Bridge: zones visibility
  useEffect(() => {
    buildingRef.current?.setZonesVisible(zonesVisible)
  }, [zonesVisible])

  // Bridge: badges (occupants)
  useEffect(() => {
    const building = buildingRef.current
    if (!building) return
    console.log('[ThreeCanvas] setBadges:', badges.length)
    building.setBadges(badges)
  }, [badges])

  // Bridge: occupants visibility
  useEffect(() => {
    buildingRef.current?.setOccupantsVisible(occupantsVisible)
  }, [occupantsVisible])

  // Bridge: path segments
  useEffect(() => {
    const building = buildingRef.current
    if (!building) return
    console.log('[ThreeCanvas] setPathSegments:', pathSegments.length)
    building.setPathSegments(pathSegments)
    building.setPathSegmentsVisible(pathSegmentsVisible)
  }, [pathSegments])

  // Bridge: path segments visibility
  useEffect(() => {
    buildingRef.current?.setPathSegmentsVisible(pathSegmentsVisible)
  }, [pathSegmentsVisible])

  // Bridge: explode
  useEffect(() => {
    buildingRef.current?.setExplode(explodeValue)
  }, [explodeValue])

  // Bridge: selected floor visual feedback
  useEffect(() => {
    buildingRef.current?.setSelectedFloor(selectedFloor)
  }, [selectedFloor])

  // Bridge: visibility
  useEffect(() => {
    buildingRef.current?.setWallsVisible(wallsVisible)
  }, [wallsVisible])

  useEffect(() => {
    buildingRef.current?.setIFCWallsVisible(ifcWallsVisible)
  }, [ifcWallsVisible])

  // Bridge: IFC rotation (real-time preview)
  useEffect(() => {
    buildingRef.current?.wallRenderer.setIFCRotation(ifcRotation)
  }, [ifcRotation])

  useEffect(() => {
    buildingRef.current?.setFloorplansVisible(floorplansVisible)
  }, [floorplansVisible])

  // Bridge: isolated floor
  useEffect(() => {
    buildingRef.current?.setIsolatedFloor(isolatedFloor)
  }, [isolatedFloor])

  // Bridge: 2D/3D mode with animated transition
  useEffect(() => {
    const controls = controlsRef.current
    if (!controls) return

    controls.animateTo2D(is2DMode)

    if (is2DMode) {
      controls.orbitControls.enableRotate = false
    } else {
      controls.orbitControls.enableRotate = true
    }
  }, [is2DMode])

  // Bridge: edit mode
  useEffect(() => {
    const controls = controlsRef.current
    if (!controls) return
    controls.editMode = editMode
  }, [editMode])

  // Bridge: wall handle display when wall selected in edit mode
  useEffect(() => {
    const building = buildingRef.current
    const controls = controlsRef.current
    if (!building || !controls) return

    if (selectedWall && editMode === 'wall') {
      const floorGroup = building.getFloorGroup(selectedWall.floor)
      if (floorGroup) {
        building.wallRenderer.showHandles(selectedWall.id, selectedWall, floorGroup)
        controls.setHandleTargets(building.wallRenderer.getHandlesGroup().children as THREE.Mesh[])
      }
    } else {
      building.wallRenderer.hideHandles()
      controls.setHandleTargets([])
    }
  }, [selectedWall, editMode])

  // Bridge: click handlers (viewer mode + edit mode)
  useEffect(() => {
    const controls = controlsRef.current
    if (!controls) return

    controls.onZoneClick = (zoneId: string) => {
      const loc = useBuildingStore.getState().locations.find((l) => l.id === zoneId)
      if (loc) {
        selectZone(loc)
        if (useSelectionStore.getState().editMode === 'none') {
          setActiveTab('zones')
        }
      }
    }

    controls.onWallClick = (wallId: string) => {
      const wall = useBuildingStore.getState().walls.find((w) => w.id === wallId)
      if (wall) {
        selectWall(wall)
        if (useSelectionStore.getState().editMode === 'none') {
          setActiveTab('murs')
        }
      }
    }

    controls.onOccupantClick = (badgeCode: string) => {
      const building = buildingRef.current
      if (!building) return
      const occupant = building.occupants.get(badgeCode)
      if (occupant) {
        const pos = occupant.group.position
        const focusFn = useViewStore.getState().focusCameraFn
        focusFn?.(pos.x, pos.y, pos.z)
        setActiveTab('personnel')
      }
    }

    controls.onZoneDragEnd = async (zoneId: string, x: number, z: number) => {
      const siteId = useBuildingStore.getState().currentSiteId
      if (!siteId) return
      try {
        await zoneApi.updateLocation(siteId, zoneId, { x, z })
        await useBuildingStore.getState().loadSite(siteId)
      } catch {
        // Revert on error - site will reload
      }
    }

    // Wall handle drag: update wall dimensions/rotation in real-time
    controls.onHandleDrag = (handleType: 'left' | 'right' | 'rotate', deltaX: number, deltaZ: number) => {
      const wall = useSelectionStore.getState().selectedWall
      if (!wall) return

      const building = buildingRef.current
      if (!building) return

      const cos = Math.cos(wall.rotation)
      const sin = Math.sin(wall.rotation)
      // Project delta onto wall's local X axis
      const localDeltaX = deltaX * cos + deltaZ * sin

      if (handleType === 'left' || handleType === 'right') {
        const sign = handleType === 'left' ? -1 : 1
        const widthDelta = localDeltaX * sign
        const newWidth = Math.max(0.1, wall.width + widthDelta)
        // Shift center to keep opposite handle fixed
        const shiftX = (widthDelta / 2) * cos * sign
        const shiftZ = (widthDelta / 2) * sin * sign

        const updatedWall = { ...wall, width: newWidth, x: wall.x + shiftX, z: wall.z + shiftZ }
        useSelectionStore.getState().selectWall(updatedWall)
      } else if (handleType === 'rotate') {
        // Rotation: compute angle from center to mouse delta
        const angleChange = Math.atan2(deltaX, deltaZ)
        const newRotation = wall.rotation + angleChange
        const updatedWall = { ...wall, rotation: newRotation }
        useSelectionStore.getState().selectWall(updatedWall)
      }
    }

    // Wall handle drag end: persist to API
    controls.onHandleDragEnd = async (_handleType: 'left' | 'right' | 'rotate') => {
      const wall = useSelectionStore.getState().selectedWall
      const siteId = useBuildingStore.getState().currentSiteId
      if (!wall || !siteId) return

      try {
        await wallApi.updateWall(siteId, wall.id, {
          x: wall.x,
          z: wall.z,
          width: wall.width,
          rotation: wall.rotation,
        })
        await useBuildingStore.getState().loadSite(siteId)
      } catch {
        // Revert on error
      }
    }

    // Plane click: dispatch for path placement
    controls.onPlaneClick = (x: number, z: number, floor: number) => {
      window.dispatchEvent(new CustomEvent('path-plane-click', { detail: { x, z, floor } }))
    }
  }, [selectZone, selectWall, setActiveTab])

  // Bridge: realtime positions -> occupant movement
  useEffect(() => {
    const building = buildingRef.current
    if (!building || positions.size === 0) return

    positions.forEach((pos, badgeCode) => {
      const occupant = building.occupants.get(badgeCode)
      if (!occupant) return

      const zone = [...building.zones.values()].find(
        (z) => z.data.name === pos.locationName
      )

      if (zone) {
        const targetPos = new THREE.Vector3(
          zone.data.x,
          0.3,
          zone.data.z
        )
        occupant.moveTo(targetPos)
        occupant.currentZoneId = zone.data.id
      }

      if (pos.battery < 20) {
        occupant.setStatus('alerte')
      }
    })
  }, [positions])

  // Bridge: topology visualization (filtered by RSSI + optimal routes)
  useEffect(() => {
    const topology = topologyRef.current
    if (!topology) return

    let filteredEdges = topologyEdges.filter((edge) => edge.rssi >= rssiThreshold)

    if (showOptimalOnly && filteredEdges.length > 0) {
      const bestEdgePerNode = new Map<string, typeof filteredEdges[number]>()
      for (const edge of filteredEdges) {
        const existing = bestEdgePerNode.get(edge.from)
        if (!existing || edge.rssi > existing.rssi) {
          bestEdgePerNode.set(edge.from, edge)
        }
      }
      filteredEdges = [...bestEdgePerNode.values()]
    }

    topology.setTopology(topologyNodes, filteredEdges)
  }, [topologyNodes, topologyEdges, rssiThreshold, showOptimalOnly])

  useEffect(() => {
    topologyRef.current?.setVisible(showTopology)
  }, [showTopology])

  // Bridge: route rendering
  useEffect(() => {
    const routeRenderer = routeRef.current
    if (!routeRenderer) return

    if (currentRoute && currentRoute.length >= 2) {
      routeRenderer.showRoute(currentRoute)
    } else {
      routeRenderer.clearRoute()
    }
  }, [currentRoute])

  // Bridge: filter offline badges
  useEffect(() => {
    const building = buildingRef.current
    if (!building) return

    if (!filterOffline) {
      for (const occ of building.occupants.values()) {
        occ.setVisible(true)
      }
      return
    }

    const now = Date.now()
    const OFFLINE_THRESHOLD = 5 * 60 * 1000
    for (const [badgeCode, occ] of building.occupants) {
      const pos = positions.get(badgeCode)
      if (!pos || now - pos.timestamp > OFFLINE_THRESHOLD) {
        occ.setVisible(false)
      } else if (pos.rssi < rssiThreshold) {
        occ.setVisible(false)
      } else {
        occ.setVisible(true)
      }
    }
  }, [filterOffline, positions, rssiThreshold])

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      {hoveredObject && <Tooltip3D info={hoveredObject} />}
    </div>
  )
}
